import RegistrationPushPrompt from '../components/RegistrationPushPrompt';
import {
    Alert, Box, Button, Checkbox, Divider, FormControl, FormLabel, Input,
    Link,
    Option, Select, Stack, Typography,
} from "@mui/joy";
import { useContext, useEffect, useRef, useState } from "react";
import { LayoutContext } from "../context";
import { isApiError } from "../utils/apiError";
import {
    APIGetCitys, APIGetTimezones,
    APILogin, APIRegisterSurveyor,
    APIRequestEmployeeCall, APIRequestEmployeeEmailCode,
    APIRequestFirstPassword, APIVerifyEmployeeCode,
    APIResetPassword,
} from "../api";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import Cookies from "js-cookie";
import { closeSidebar } from "../utils";
import MaskedInput from "../components/JoyMaskedInput";

import ArrowBackIosRoundedIcon  from '@mui/icons-material/ArrowBackIosRounded';
import EmailRoundedIcon         from "@mui/icons-material/EmailRounded";
import LockRoundedIcon          from "@mui/icons-material/LockRounded";
import PhoneRoundedIcon         from "@mui/icons-material/PhoneRounded";
import VisibilityRoundedIcon    from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import PersonRoundedIcon        from "@mui/icons-material/PersonRounded";
import ShieldRoundedIcon        from "@mui/icons-material/ShieldRounded";
import ContentCopyRoundedIcon   from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon         from "@mui/icons-material/CheckRounded";
import { useNavigate } from "react-router";
import { Link as RouterLink } from "react-router";
import { setAuthToken } from "../auth";


// ─── Цвета бренда ─────────────────────────────────────────────────────────────
const BRAND  = "#572AC3";
const BRAND2 = "#8d6aec";

// ─── Плавающие частицы ────────────────────────────────────────────────────────
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
    id: i,
    size: 2 + Math.random() * 5,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 7 + Math.random() * 10,
    delay: Math.random() * 7,
    opacity: 0.035 + Math.random() * 0.07,
}));

function FloatingParticles() {
    return (
        <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
            {PARTICLES.map(p => (
                <motion.div
                    key={p.id}
                    style={{
                        position: "absolute",
                        left: `${p.x}%`, top: `${p.y}%`,
                        width: p.size, height: p.size,
                        borderRadius: "50%",
                        backgroundColor: BRAND,
                        opacity: p.opacity,
                    }}
                    animate={{
                        y: [0, -28, 0],
                        x: [0, 10, -10, 0],
                        opacity: [p.opacity, p.opacity * 2.8, p.opacity],
                        scale: [1, 1.6, 1],
                    }}
                    transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
                />
            ))}
        </Box>
    );
}

// ─── Сетка-фон ────────────────────────────────────────────────────────────────
function GridDecor() {
    return (
        <Box sx={{
            position: "absolute", inset: 0, pointerEvents: "none",
            backgroundImage: `
                linear-gradient(rgba(87,42,195,0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(87,42,195,0.05) 1px, transparent 1px)
            `,
            backgroundSize: "56px 56px",
        }} />
    );
}

// ─── Карточка с 3D-наклоном ───────────────────────────────────────────────────
function TiltCard({ children }) {
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useTransform(y, [-0.5, 0.5], [3, -3]);
    const rotateY = useTransform(x, [-0.5, 0.5], [-3, 3]);

    const handleMouseMove = (e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        x.set((e.clientX - rect.left) / rect.width - 0.5);
        y.set((e.clientY - rect.top)  / rect.height - 0.5);
    };
    const handleMouseLeave = () => { x.set(0); y.set(0); };

    return (
        <motion.div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 800 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
            {children}
        </motion.div>
    );
}

// ─── Стили лейбла ─────────────────────────────────────────────────────────────
const labelSx = {
    fontSize: "11px", fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.75,
};

// ─── Стили Input ──────────────────────────────────────────────────────────────
const inputSx = (hasError) => ({
    borderRadius: "12px", fontSize: "18px", py: 1.25,
    "--Input-focusedThickness": "1.5px", fontWeight: 500,
    "--Input-focusedHighlight": hasError ? "#ef4444" : BRAND,
});

// ─── Анимированная ошибка поля ────────────────────────────────────────────────
function FieldError({ msg }) {
    return (
        <AnimatePresence>
            {msg && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.18 }}
                >
                    <Typography sx={{ fontSize: "11px", color: "#ef4444", mt: 0.5 }}>
                        {msg}
                    </Typography>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ─── Тумблер Email / Телефон ──────────────────────────────────────────────────
function LoginTypeTabs({ value, onChange }) {
    return (
        <Box sx={{
            display: "flex",
            bgcolor: "rgba(87,42,195,0.06)",
            borderRadius: "14px",
            p: "3px", gap: "3px",
        }}>
            {["email", "phone"].map(t => (
                <Button
                    key={t}
                    size="sm"
                    variant={value === t ? "solid" : "plain"}
                    color={value === t ? "primary" : "neutral"}
                    onClick={() => onChange(t)}
                    startDecorator={t === "email"
                        ? <EmailRoundedIcon sx={{ fontSize: 14 }} />
                        : <PhoneRoundedIcon sx={{ fontSize: 14 }} />
                    }
                    sx={{
                        flex: 1, borderRadius: "11px",
                        fontSize: "12px", fontWeight: 600,
                        transition: "all 0.22s",
                        ...(value === t && {
                            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`,
                            color: "#fff",
                        }),
                    }}
                >
                    {t === "email" ? "Почта" : "Телефон"}
                </Button>
            ))}
        </Box>
    );
}

// ─── Кнопка Submit ────────────────────────────────────────────────────────────
function SubmitButton({ loading, onClick, children, disabled = false }) {
    return (
        <motion.div whileHover={{ scale: loading ? 1 : 1.02 }} whileTap={{ scale: loading ? 1 : 0.97 }}>
            <Button onClick={onClick} loading={loading} disabled={disabled}
                fullWidth
                sx={{
                    borderRadius: "12px", py: 1.4,
                    fontSize: "14px", fontWeight: 700, letterSpacing: "0.02em",
                    background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`,
                    color: "#fff", border: "none",
                    boxShadow: `0 4px 18px rgba(87,42,195,0.35)`,
                    "&:hover": { boxShadow: `0 6px 24px rgba(87,42,195,0.45)` },
                    transition: "all 0.2s",
                }}
            >
                {children}
            </Button>
        </motion.div>
    );
}


function CodeCells({ values, refs, onChange, onKeyDown, onPaste, autoFocus = true }) {
    return (
        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ my: 2 }}>
            {values.map((v, i) => (
                <Box
                    key={i}
                    component="input"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    ref={el => (refs.current[i] = el)}
                    value={v}
                    onChange={e => onChange(i, e.target.value)}
                    onKeyDown={e => onKeyDown(i, e)}
                    onPaste={i === 0 ? onPaste : undefined}
                    autoFocus={autoFocus && i === 0}
                    sx={{
                        width: 56, height: 64,
                        textAlign: "center",
                        fontSize: "28px", fontWeight: 700,
                        border: "2px solid",
                        borderColor: v ? "#572AC3" : "rgba(87,42,195,0.18)",
                        borderRadius: "16px",
                        background: v ? "rgba(87,42,195,0.05)" : "#fff",
                        color: "#1a1a1a",
                        outline: "none",
                        transition: "all .18s",
                        "&:focus": {
                            borderColor: "#572AC3",
                            boxShadow: "0 0 0 4px rgba(87,42,195,0.15)",
                            transform: "translateY(-2px)",
                        },
                    }}
                />
            ))}
        </Stack>
    );
}


// ═══════════════════════════════════════════════════════════════════════════════
export default function LockPage() {
    const { setAccount, setActiveRole, getEmployee, setTokenEmployee, setLock, setSidebarOpen } = useContext(LayoutContext);
    const navigate = useNavigate();

    // 'login' | 'register' | 'no-password' | 'password-created' | 'forgot-password' | 'password-sent' | 'call-phone' | 'call-code' | 'role-choice'
    const [registrationPushToken, setRegistrationPushToken] = useState('');
    const [mode, setMode] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('auth');
    return requested === 'register' ? 'register' : requested === 'login' ? 'login' : 'call-phone';
  });

    const [policyAgreed, setPolicyAgreed] = useState(false);

    // Тип входа: 'email' | 'phone'
    const [loginType, setLoginType] = useState("email");

    // Поля авторизации
    const [loginEmail, setLoginEmail]   = useState("");
    const [loginPhone, setLoginPhone]   = useState("");
    const [pwd, setPwd]                 = useState("");
    const [showPwd, setShowPwd]         = useState(false);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [shakeKey, setShakeKey]       = useState(0);

    // ── Восстановление пароля ─────────────────────────────────────────────────
    const [forgotPhone, setForgotPhone]           = useState("");
    const [forgotEmail, setForgotEmail]           = useState("");
    const [forgotEmailMasked, setForgotEmailMasked] = useState("");

    // Авторизация по звонку
    const [callPhone, setCallPhone] = useState("");
    const [callPhoneMasked, setCallPhoneMasked] = useState("");
    const [callCode, setCallCode] = useState(["", "", "", ""]);
    const callInputsRef = useRef([]);
    const [cooldown, setCooldown] = useState(0); // секунды до повторной отправки
    // Канал получения кода: 'phone' | 'email'
    const [callChannel, setCallChannel] = useState("phone");
    // Email-вход
    const [callEmail, setCallEmail] = useState("");
    const [callEmailMasked, setCallEmailMasked] = useState("");

    // После успешного входа — данные ответа сервера для возможного выбора роли
    const [pendingAuthData, setPendingAuthData] = useState(null);

    const ROLE_META = {
        manager:        { label: "Менеджер",        emoji: "📋", desc: "Календарь, события, заявки", route: "/" },
        surveyor:       { label: "Обмерщик",        emoji: "📐", desc: "Мои выезды и обмеры",        route: "/" },
        draftsman:      { label: "Чертежник",       emoji: "✏️", desc: "Чертежи в работе",          route: "/draftsman" },
        quality_master: { label: "Контроль качества", emoji: "✅", desc: "Проверка работ",          route: "/" },
    };

    function chooseRole(pos) {
        // Пишем напрямую — не полагаемся на switchRole из контекста,
        // он в этот момент видит stale account и делает early return
        localStorage.setItem('active_role', pos);
        setActiveRole(pos);   // оставляем — пусть стейт тоже подтянется
        finalizeLogin();
        const route = ROLE_META[pos]?.route || "/";
        navigate(route, { replace: true });
    }

    // Пароль создан
    const [generatedPassword, setGeneratedPassword] = useState("");
    const [copied, setCopied] = useState(false);

    // Поля регистрации
    const [fullName, setFullName]       = useState("");
    const [regLastName, setRegLastName] = useState("");
    const [regPatronymic, setRegPatronymic] = useState("");

    const [regPhone, setRegPhone]       = useState("");
    const [regEmail, setRegEmail]       = useState("");
    const [regPwd, setRegPwd]           = useState("");
    const [showRegPwd, setShowRegPwd]   = useState(false);
    const [cityBitrix, setCityBitrix]   = useState("");
    const [timezone, setTimezone]       = useState("");
    const [cities, setCities]           = useState([]);
    const [timezones, setTimezones]     = useState([]);
    const [regErrors, setRegErrors]     = useState({});

    // Первый вход
    const [noPassEmail, setNoPassEmail] = useState("");
    const [noPassType, setNoPassType]   = useState("email");
    const [noPassPhone, setNoPassPhone] = useState("");

    useEffect(() => {
        if (mode === "register" && cities.length === 0) {
            APIGetCitys().then(data => {
                if (Array.isArray(data))
                    setCities([...data].sort((a, b) => a.name.localeCompare(b.name, "ru")));
            });
            APIGetTimezones().then(data => {
                if (Array.isArray(data)) setTimezones(data);
            });
        }
    }, [mode]);

    function switchMode(m) {
        setError("");
        setFieldErrors({});
        setRegErrors({});
        setMode(m);
    }

    function finalizeLogin() {
        setLock(false);
        setSidebarOpen(false);
        closeSidebar();
        getEmployee();
    }

    function applyAuthSuccess(data) {
        setAuthToken(data.token_employee);
        setTokenEmployee(data.token_employee);
        setAccount(data.employee);

        // Несколько ролей — показываем выбор
        if (data.role_choice_required && Array.isArray(data.positions) && data.positions.length > 1) {
            setPendingAuthData(data);
            setMode("role-choice");
            return;
        }

        // Одна роль — фиксируем её сразу, чтобы при refresh всё работало
        const onlyPos = data.employee?.position?.[0];
        if (onlyPos) {
            localStorage.setItem('active_role', onlyPos);
        }

        finalizeLogin();
    }

    function requestCall() {
        setError("");

        // Канал «телефон» — как было
        if (callChannel === "phone") {
            if (callPhone.replace(/\D/g, "").length < 11) {
                setError("Введите корректный номер");
                setShakeKey(k => k + 1);
                return;
            }
            setLoading(true);
            APIRequestEmployeeCall(callPhone).then(data => {
                setLoading(false);
                if (data?.status === "ok") {
                    setCallPhoneMasked(data.phone_masked || callPhone);
                    setCallCode(["", "", "", ""]);
                    setCooldown(data.cooldown || 60);
                    setMode("call-code");
                } else {
                    if (data?.retry_after) setCooldown(data.retry_after);
                    setError(data?.detail || "Ошибка отправки звонка");
                    setShakeKey(k => k + 1);
                }
            });
            return;
        }

        // Канал «email» — новое
        const emailTrimmed = callEmail.trim();
        // Простая валидация email на фронте — серверная всё равно проверит
        if (!emailTrimmed || !/^\S+@\S+\.\S+$/.test(emailTrimmed)) {
            setError("Введите корректный email");
            setShakeKey(k => k + 1);
            return;
        }
        setLoading(true);
        APIRequestEmployeeEmailCode(emailTrimmed).then(data => {
            setLoading(false);
            if (data?.status === "ok") {
                setCallEmailMasked(data.email_masked || emailTrimmed);
                setCallCode(["", "", "", ""]);
                setCooldown(data.cooldown || 60);
                setMode("call-code");
            } else {
                if (data?.retry_after) setCooldown(data.retry_after);
                setError(data?.detail || "Ошибка отправки кода на почту");
                setShakeKey(k => k + 1);
            }
        });
    }

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    function handleCodeChange(idx, value) {
        const v = value.replace(/\D/g, "").slice(0, 1);
        const next = [...callCode];
        next[idx] = v;
        setCallCode(next);
        if (v && idx < 3) callInputsRef.current[idx + 1]?.focus();
        if (next.every(d => d) && next.join("").length === 4) {
            verifyCode(next.join(""));
        }
    }

    function handleCodeKeyDown(idx, e) {
        if (e.key === "Backspace" && !callCode[idx] && idx > 0) {
            callInputsRef.current[idx - 1]?.focus();
        }
    }

    function handleCodePaste(e) {
        const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
        if (pasted.length) {
            e.preventDefault();
            const arr = pasted.padEnd(4, "").split("").slice(0, 4);
            setCallCode(arr);
            if (arr.join("").length === 4) verifyCode(arr.join(""));
            else callInputsRef.current[arr.findIndex(c => !c)]?.focus();
        }
    }

    function verifyCode(code) {
        setLoading(true);
        setError("");
        const payload = callChannel === "email"
            ? { email: callEmail.trim().toLowerCase(), code }
            : { phone: callPhone, code };

        APIVerifyEmployeeCode(payload).then(data => {
            setLoading(false);
            if (data?.token) {
                applyAuthSuccess(data);
                return;
            }
            const err = isApiError(data) ? data : { message: data?.detail };
            setError(err.message || "Код неверный или устарел. Запросите новый.");
            setCallCode(["", "", "", ""]);
            callInputsRef.current[0]?.focus();
            setShakeKey(k => k + 1);
        });
    }

    // ── Логин ─────────────────────────────────────────────────────────────────
    function validateLogin() {
        const errs = {};
        if (loginType === "email" && !loginEmail.trim()) errs.login = "Введите email";
        if (loginType === "phone" && loginPhone.replace(/\D/g, "").length < 11) errs.login = "Введите корректный номер";
        if (!pwd.trim()) errs.pwd = "Введите пароль";
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    }

    function entryLk() {
        setError("");
        if (!validateLogin()) { setShakeKey(k => k + 1); return; }
        setLoading(true);
        // Передаём login — email или телефон (сервер сам разберёт в поле `login`)
        const login = loginType === "email" ? loginEmail : loginPhone;
        APILogin(login, pwd).then(data => {
            setLoading(false);

            if (data?.token) {
                applyAuthSuccess(data);
                return;
            }

            // Нормализованная ошибка: показываем текст сервера как есть
            const err = isApiError(data) ? data : { message: data?.message || data?.detail };
            setError(err.message || "Неверные данные для входа");

            // Подсветка конкретного поля по коду ошибки
            if (err.code === "wrong_password" || err.code === "password_required") {
                setFieldErrors(p => ({ ...p, pwd: err.message }));
            } else if (err.code === "not_found" || err.code === "login_required") {
                setFieldErrors(p => ({ ...p, login: err.message }));
            }

            // Пароль ещё не создавался — сразу подсказываем нужный сценарий
            if (err.code === "password_not_set") {
                setError(`${err.message}`);
            }

            setShakeKey(k => k + 1);
        });
    }

    // ── Регистрация ───────────────────────────────────────────────────────────
    function validateRegister() {
        const errs = {};
        if (!fullName.trim())     errs.fullName     = "Введите имя";
        if (!regLastName.trim())  errs.regLastName  = "Введите фамилию";
        if (regPhone.replace(/\D/g, "").length < 11) errs.regPhone = "Введите корректный номер";
        if (!regEmail.trim()) errs.regEmail = "Введите email";
        if (!cityBitrix)      errs.cityBitrix = "Выберите город";
        if (!timezone)        errs.timezone = "Выберите часовой пояс";
        if (!regPwd.trim())   errs.regPwd = "Придумайте пароль";
        setRegErrors(errs);
        return Object.keys(errs).length === 0;
    }

    function registerFn() {
        if (!policyAgreed) {
            setError("Необходимо согласиться с политикой конфиденциальности");
            return;
        }

        setError("");
        if (!validateRegister()) { setShakeKey(k => k + 1); return; }
        setLoading(true);
        APIRegisterSurveyor({
            name: fullName,
            last_name: regLastName,
            patronymic: regPatronymic || undefined,
            phone: regPhone, email: regEmail,
            city_bitrix: cityBitrix, timezone, password: regPwd,
        }).then(data => {
            setLoading(false);

            if (data?.employee_id) {
                setRegistrationPushToken(data.registration_push_token || '');
                setMode("login");
                setLock(true);
                return;
            }

            const err = isApiError(data) ? data : { message: data?.detail || data?.message, fields: {} };
            setError(err.message || "Не удалось отправить анкету");

            // Раскладываем ошибки по конкретным полям формы
            const map = {
                name: "fullName", last_name: "regLastName", patronymic: "regPatronymic",
                phone: "regPhone", email: "regEmail", city_bitrix: "cityBitrix",
                timezone: "timezone", password: "regPwd",
            };
            const nextFieldErrors = {};
            Object.entries(err.fields || {}).forEach(([key, text]) => {
                const uiKey = map[key];
                if (uiKey) nextFieldErrors[uiKey] = text;
            });
            if (Object.keys(nextFieldErrors).length) {
                setRegErrors(p => ({ ...p, ...nextFieldErrors }));
            }

            setShakeKey(k => k + 1);
        });
    }

    // ── Первый вход ───────────────────────────────────────────────────────────
    function requestFirstPassword() {
        const login = noPassType === "email" ? noPassEmail : noPassPhone;

        if (!login.trim() || (noPassType === "phone" && login.replace(/\D/g, "").length < 11)) {
            setError(noPassType === "email" ? "Введите email" : "Введите корректный номер");
            setShakeKey(k => k + 1);
            return;
        }
        setLoading(true);
        setError("");
        APIRequestFirstPassword(login).then(data => {
            setLoading(false);
            if (data?.password) {
                setGeneratedPassword(data.password);
                setCopied(false);
                setMode("password-created");
                return;
            }
            const err = isApiError(data) ? data : { message: data?.detail || data?.error };
            setError(err.message || "Не удалось создать пароль. Обратитесь к менеджеру.");
            setShakeKey(k => k + 1);
        });
    }

    function requestPasswordReset() {
        setError("");
        const emailTrimmed = forgotEmail.trim();
        const phoneDigits  = forgotPhone.replace(/\D/g, "");

        if (!emailTrimmed || !/^\S+@\S+\.\S+$/.test(emailTrimmed)) {
            setError("Введите корректный email");
            setShakeKey(k => k + 1);
            return;
        }
        if (phoneDigits.length < 11) {
            setError("Введите корректный номер телефона");
            setShakeKey(k => k + 1);
            return;
        }

        setLoading(true);
        APIResetPassword({ phone: forgotPhone, email: emailTrimmed }).then(data => {
            setLoading(false);
            if (data?.status === "ok") {
                setForgotEmailMasked(data.email_masked || emailTrimmed);
                setMode("password-sent");
            } else {
                // 404 / 400 — связка не найдена либо невалидные данные
                setError(data?.detail || "Сотрудник не найден. Проверьте телефон и email.");
                setShakeKey(k => k + 1);
            }
        });
    }

    function handleCopy() {
        navigator.clipboard.writeText(generatedPassword).then(() => setCopied(true));
    }

    function handleGoLogin() {
        const confirmed = window.confirm("Вы скопировали пароль? После нажатия «ОК» он больше не будет показан.");
        if (confirmed) {
            setPwd("");
            setGeneratedPassword("");
            setCopied(false);
            switchMode("login");
        }
    }

    // ── Анимации ──────────────────────────────────────────────────────────────
    const containerVariants = {
        hidden:  {},
        visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
    };
    const itemVariants = {
        hidden:  { opacity: 1 },
        visible: { opacity: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
    };
    const shakeVariants = {
        shake: { x: [0, -10, 10, -8, 8, -4, 4, 0], transition: { duration: 0.45 } },
        idle:  { x: 0 },
    };

    return (
        <Box sx={{
            minHeight: "100dvh", width: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", overflow: "hidden",
            background: "linear-gradient(145deg, #f5rgba(245, 243, 255, 0.65), #ede9fe99 40%, #f0f0ffa0 100%)",
        }}>
            {registrationPushToken && <Box sx={{ position: 'fixed', zIndex: 1500, right: 12, bottom: 12, width: 'min(420px, calc(100vw - 24px))', maxHeight: '80vh', overflow: 'auto' }}>
                <RegistrationPushPrompt token={registrationPushToken} />
                <Button size="sm" variant="plain" onClick={() => setRegistrationPushToken('')}>Закрыть</Button>
            </Box>}
            <GridDecor />
            <FloatingParticles />

            {/* Glow blobs */}
            <Box sx={{
                position: "absolute", top: "-20%", left: "-15%",
                width: 560, height: 560, borderRadius: "50%", pointerEvents: "none",
                background: `radial-gradient(circle, rgba(87,42,195,0.1) 0%, transparent 65%)`,
            }} />
            <Box sx={{
                position: "absolute", bottom: "-25%", right: "-10%",
                width: 640, height: 640, borderRadius: "50%", pointerEvents: "none",
                background: `radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 65%)`,
            }} />

            {/* Shake wrapper */}
            <motion.div
                key={shakeKey}
                variants={shakeVariants}
                animate={error ? "shake" : "idle"}
                style={{ width: "100%", maxWidth: 440, padding: "24px 16px", position: "relative", zIndex: 1 }}
            >
                <motion.div variants={containerVariants} initial="hidden" animate="visible">

                    {/* ── Лого ── */}
                    <motion.div variants={itemVariants}>
                        <Stack alignItems="center" sx={{ mb: 4 }}>
                            <motion.div
                                initial={{ scale: 0, rotate: -25 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.05, type: "spring", stiffness: 260, damping: 18 }}
                            >
                                <Box
                                    component="img"
                                    src="./logo191.jpg"
                                    sx={{
                                        width: 80, height: 80, borderRadius: "20px",
                                        objectFit: "cover",
                                        boxShadow: `0 2px 18px rgba(87,42,195,0.35)`,
                                    }}
                                />
                            </motion.div>
                        </Stack>
                    </motion.div>

                    {/* ── Табы Авторизация / Регистрация ── */}
                    {(mode === "login" || mode === "register") && (
                        <motion.div variants={itemVariants}>
                            <Box sx={{
                                display: "flex", bgcolor: "rgba(87,42,195,0.07)",
                                borderRadius: "16px", p: "4px", gap: "4px", mb: 2,
                            }}>
                                <Button variant="plain" color="warning" onClick={() => switchMode("call-phone")}>
                                    ← Войти по звонку
                                </Button>
                                {["login", "register"].map(m => (
                                    <Button
                                        key={m}
                                        size="sm"
                                        variant={mode === m ? "solid" : "plain"}
                                        color={mode === m ? "primary" : "neutral"}
                                        onClick={() => switchMode(m)}
                                        sx={{
                                            flex: 1, borderRadius: "12px", fontSize: "13px", fontWeight: 600,
                                            transition: "all 0.25s",
                                            ...(mode === m && {
                                                background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`,
                                                color: "#fff",
                                            }),
                                        }}
                                    >
                                        {m === "login" ? "Авторизация" : "Анкета"}
                                    </Button>
                                ))}
                            </Box>
                        </motion.div>
                    )}

                    {/* ── Карточка ── */}
                    <motion.div variants={itemVariants}>
                        <TiltCard>
                            <Box sx={{
                                p: { xs: 2.5, sm: 3.5 },
                                borderRadius: "24px",
                                border: "1px solid rgba(87,42,195,0.12)",
                                bgcolor: "#ffffff",
                                boxShadow: "0 24px 64px rgba(87,42,195,0.12), inset 0 1px 0 rgba(255,255,255,0.95)",
                                maxHeight: "72vh",
                                overflowY: "auto",
                            }}>
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={mode}
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -20, opacity: 0 }}
                                        transition={{ duration: 0.22 }}
                                    >

                                        {/* Общий блок ошибки — виден в ЛЮБОМ режиме формы.
                                            Раньше он был только в login/register, поэтому при
                                            входе по звонку/коду форма молча тряслась. */}
                                        <AnimatePresence>
                                            {error && (
                                                <motion.div key="global-err"
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.22 }}
                                                    style={{ overflow: "hidden" }}
                                                >
                                                    <Alert
                                                        color="danger"
                                                        variant="soft"
                                                        sx={{ borderRadius: "12px", fontSize: "13px", mb: 2, alignItems: "flex-start" }}
                                                    >
                                                        {error}
                                                    </Alert>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        {mode === "call-phone" && (
                                            <motion.div variants={itemVariants}>
                                                <Stack spacing={2}>
                                                    <Typography level="h3" sx={{ fontWeight: 700 }}>
                                                        {callChannel === "phone" ? "Вход по звонку" : "Вход по почте"}
                                                    </Typography>

                                                    <LoginTypeTabs
                                                        value={callChannel === "phone" ? "phone" : "email"}
                                                        onChange={t => {
                                                            setCallChannel(t);
                                                            setError("");
                                                        }}
                                                    />

                                                    <Typography level="body-sm" textColor="neutral.500">
                                                        {callChannel === "phone"
                                                            ? "Введите номер. Мы позвоним — введите 4 последние цифры входящего номера."
                                                            : "Введите почту. Мы пришлём 4-значный код на ваш email."}
                                                    </Typography>

                                                    {callChannel === "phone" ? (
                                                        <MaskedInput
                                                            mask="+7 (000) 000-00-00"
                                                            placeholder="+7 (000) 000-00-00"
                                                            value={callPhone}
                                                            onChange={e => setCallPhone(e.target.value)}
                                                            startDecorator={<PhoneRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                            sx={inputSx(!!regErrors.regPhone)}
                                                        />
                                                    ) : (
                                                        <Input
                                                            type="email"
                                                            placeholder="you@example.com"
                                                            value={callEmail}
                                                            onChange={e => setCallEmail(e.target.value)}
                                                            startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                            sx={inputSx(false)}
                                                        />
                                                    )}

                                                    <Stack direction={"column"} sx={{ gap: 1 }}>
                                                        <Button
                                                            loading={loading}
                                                            onClick={requestCall}
                                                            size="lg"
                                                            sx={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`, borderRadius: 14 }}
                                                        >
                                                            {callChannel === "phone" ? "Получить звонок" : "Получить код на почту"}
                                                        </Button>

                                                        <Button
                                                            variant="soft"
                                                            color="neutral"
                                                            size="lg"
                                                            onClick={() => switchMode("login")}
                                                            sx={{ borderRadius: 14 }}
                                                        >
                                                            Войти по паролю
                                                        </Button>

                                                        <Button
                                                            variant="outlined"
                                                            color="neutral"
                                                            size="lg"
                                                            onClick={() => switchMode("register")}
                                                            sx={{ borderRadius: 14 }}
                                                        >
                                                            Заполнить анкету
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            </motion.div>
                                        )}

                                        {mode === "call-code" && (
                                            <motion.div variants={itemVariants}>
                                                <Button 
                                                    variant="soft" 
                                                    startDecorator={<ArrowBackIosRoundedIcon />} 
                                                    onClick={() => switchMode("call-phone")}
                                                    sx={{ pl: 1, pr: 1.8, borderRadius: 12 }}
                                                >
                                                    {callChannel === "phone" ? "Изменить номер" : "Изменить email"}
                                                </Button>

                                                <Typography level="h3" sx={{ mt: 1, fontWeight: 700 }}>Подтвердите вход</Typography>
                                                <Typography level="body-sm">
                                                    {callChannel === "phone" ? (
                                                        <>
                                                            Звоним на <b>{callPhoneMasked}</b>.<br/>
                                                            Введите <b>последние 4 цифры</b> входящего номера
                                                        </>
                                                    ) : (
                                                        <>
                                                            Письмо отправлено на <b>{callEmailMasked}</b>.<br/>
                                                            Введите <b>4-значный код</b> из письма
                                                        </>
                                                    )}
                                                </Typography>

                                                <CodeCells
                                                    values={callCode}
                                                    refs={callInputsRef}
                                                    onChange={handleCodeChange}
                                                    onKeyDown={handleCodeKeyDown}
                                                    onPaste={handleCodePaste}
                                                />

                                                <Button
                                                    size='lg'
                                                    variant="soft"
                                                    disabled={cooldown > 0 || loading}
                                                    onClick={requestCall}
                                                    fullWidth
                                                    sx={{ borderRadius: 14 }}
                                                >
                                                    {cooldown > 0
                                                        ? `Повторить через ${cooldown} сек.`
                                                        : (callChannel === "phone" ? "Позвонить ещё раз" : "Отправить код ещё раз")}
                                                </Button>
                                            </motion.div>
                                        )}

                                        {/* ──────── АВТОРИЗАЦИЯ ──────── */}
                                        {mode === "login" && (
                                            <Stack spacing={2.5} onKeyDown={e => { if (e.key === "Enter") entryLk(); }}>

                                                <LoginTypeTabs value={loginType} onChange={t => {
                                                    setLoginType(t);
                                                    setFieldErrors({});
                                                    setError("");
                                                }} />

                                                {/* Email или Телефон */}
                                                <AnimatePresence mode="wait">
                                                    {loginType === "email" ? (
                                                        <motion.div key="email-field"
                                                            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: 10 }} transition={{ duration: 0.2 }}
                                                        >
                                                            <FormControl error={!!fieldErrors.login}>
                                                                <FormLabel sx={labelSx}>Email</FormLabel>
                                                                <Input
                                                                    type="email" placeholder="ivan@example.com" autoFocus
                                                                    value={loginEmail}
                                                                    onChange={e => { setLoginEmail(e.target.value); setFieldErrors(p => ({ ...p, login: "" })); }}
                                                                    startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                                    sx={inputSx(!!fieldErrors.login)}
                                                                />
                                                                <FieldError msg={fieldErrors.login} />
                                                            </FormControl>
                                                        </motion.div>
                                                    ) : (
                                                        <motion.div key="phone-field"
                                                            initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}
                                                        >
                                                            <FormControl error={!!fieldErrors.login}>
                                                                <FormLabel sx={labelSx}>Телефон</FormLabel>
                                                                <MaskedInput
                                                                    mask="80000000000"
                                                                    placeholder="8__________"
                                                                    value={loginPhone}
                                                                    onChange={e => { setLoginPhone(e.target.value); setFieldErrors(p => ({ ...p, login: "" })); }}
                                                                    startDecorator={<PhoneRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                                    sx={inputSx(!!fieldErrors.login)}
                                                                />
                                                                <FieldError msg={fieldErrors.login} />
                                                            </FormControl>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>

                                                {/* Пароль */}
                                                <FormControl error={!!fieldErrors.pwd}>
                                                    <FormLabel sx={labelSx}>Пароль</FormLabel>
                                                    <Input
                                                        type={showPwd ? "text" : "password"} placeholder="••••••••"
                                                        value={pwd}
                                                        onChange={e => { setPwd(e.target.value); setFieldErrors(p => ({ ...p, pwd: "" })); }}
                                                        startDecorator={<LockRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        endDecorator={
                                                            <Box onClick={() => setShowPwd(v => !v)} sx={{
                                                                cursor: "pointer", display: "flex", color: "rgba(0,0,0,0.3)",
                                                                "&:hover": { color: "rgba(0,0,0,0.6)" }, transition: "color 0.15s",
                                                            }}>
                                                                {showPwd
                                                                    ? <VisibilityOffRoundedIcon sx={{ fontSize: 17 }} />
                                                                    : <VisibilityRoundedIcon    sx={{ fontSize: 17 }} />
                                                                }
                                                            </Box>
                                                        }
                                                        sx={inputSx(!!fieldErrors.pwd)}
                                                    />
                                                    <FieldError msg={fieldErrors.pwd} />
                                                </FormControl>

                                                <SubmitButton loading={loading} onClick={entryLk}>Войти</SubmitButton>

                                                <Button
                                                    variant="plain" color="neutral" size="sm"
                                                    onClick={() => switchMode("no-password")}
                                                    sx={{ fontSize: "12px", color: `${BRAND}99`, "&:hover": { color: BRAND } }}
                                                >
                                                    Нет пароля / первый вход →
                                                </Button>

                                                <Button
                                                    variant="plain" color="neutral" size="sm"
                                                    onClick={() => switchMode("forgot-password")}
                                                    sx={{ fontSize: "12px", color: `${BRAND}99`, "&:hover": { color: BRAND } }}
                                                >
                                                    Забыли пароль?
                                                </Button>
                                            </Stack>
                                        )}

                                        {/* ──────── РЕГИСТРАЦИЯ ──────── */}
                                        {mode === "register" && (
                                            <Stack spacing={2} onKeyDown={e => { if (e.key === "Enter") registerFn(); }}>

                                                <FormControl error={!!regErrors.regLastName}>
                                                    <FormLabel sx={labelSx}>Фамилия *</FormLabel>
                                                    <Input
                                                        placeholder="Иванов" value={regLastName}
                                                        onChange={e => { setRegLastName(e.target.value); setRegErrors(p => ({ ...p, regLastName: "" })); }}
                                                        startDecorator={<PersonRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        sx={inputSx(!!regErrors.regLastName)}
                                                    />
                                                    <FieldError msg={regErrors.regLastName} />
                                                </FormControl>

                                                <FormControl error={!!regErrors.fullName}>
                                                    <FormLabel sx={labelSx}>Имя *</FormLabel>
                                                    <Input
                                                        placeholder="Иван" value={fullName}
                                                        onChange={e => { setFullName(e.target.value); setRegErrors(p => ({ ...p, fullName: "" })); }}
                                                        startDecorator={<PersonRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        sx={inputSx(!!regErrors.fullName)}
                                                    />
                                                    <FieldError msg={regErrors.fullName} />
                                                </FormControl>

                                                <FormControl>
                                                    <FormLabel sx={labelSx}>Отчество</FormLabel>
                                                    <Input
                                                        placeholder="Иванович" value={regPatronymic}
                                                        onChange={e => setRegPatronymic(e.target.value)}
                                                        startDecorator={<PersonRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        sx={inputSx(false)}
                                                    />
                                                </FormControl>

                                                <FormControl error={!!regErrors.regPhone}>
                                                    <FormLabel sx={labelSx}>Телефон</FormLabel>
                                                    <MaskedInput
                                                        mask="80000000000" placeholder="8__________" value={regPhone}
                                                        onChange={e => { setRegPhone(e.target.value); setRegErrors(p => ({ ...p, regPhone: "" })); }}
                                                        startDecorator={<PhoneRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        sx={inputSx(!!regErrors.regPhone)}
                                                    />
                                                    <FieldError msg={regErrors.regPhone} />
                                                </FormControl>

                                                <FormControl error={!!regErrors.regEmail}>
                                                    <FormLabel sx={labelSx}>Email</FormLabel>
                                                    <Input
                                                        type="email" placeholder="ivan@example.com" value={regEmail}
                                                        onChange={e => { setRegEmail(e.target.value); setRegErrors(p => ({ ...p, regEmail: "" })); }}
                                                        startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        sx={inputSx(!!regErrors.regEmail)}
                                                    />
                                                    <FieldError msg={regErrors.regEmail} />
                                                </FormControl>

                                                <FormControl error={!!regErrors.cityBitrix}>
                                                    <FormLabel sx={labelSx}>Город</FormLabel>
                                                    <Select
                                                        value={cityBitrix}
                                                        onChange={(_, v) => { setCityBitrix(v); setRegErrors(p => ({ ...p, cityBitrix: "" })); }}
                                                        placeholder="Выберите город"
                                                        sx={{ borderRadius: "12px", fontSize: "14px" }}
                                                        slotProps={{ listbox: { sx: { zIndex: 100001 } } }}
                                                    >
                                                        {cities.map(city => (
                                                            <Option key={city.id} value={city.id}>{city.name}</Option>
                                                        ))}
                                                    </Select>
                                                    <FieldError msg={regErrors.cityBitrix} />
                                                </FormControl>

                                                <FormControl error={!!regErrors.timezone}>
                                                    <FormLabel sx={labelSx}>Часовой пояс</FormLabel>
                                                    <Select
                                                        value={timezone}
                                                        onChange={(_, v) => { setTimezone(v); setRegErrors(p => ({ ...p, timezone: "" })); }}
                                                        placeholder="Выберите часовой пояс"
                                                        sx={{ borderRadius: "12px", fontSize: "14px" }}
                                                        slotProps={{ listbox: { sx: { zIndex: 100001 } } }}
                                                    >
                                                        {timezones.map(tz => (
                                                            <Option key={tz[0]} value={tz[0]}>{tz[1]}</Option>
                                                        ))}
                                                    </Select>
                                                    <FieldError msg={regErrors.timezone} />
                                                </FormControl>

                                                <FormControl error={!!regErrors.regPwd}>
                                                    <FormLabel sx={labelSx}>Пароль</FormLabel>
                                                    <Input
                                                        type={showRegPwd ? "text" : "password"} placeholder="••••••••" value={regPwd}
                                                        onChange={e => { setRegPwd(e.target.value); setRegErrors(p => ({ ...p, regPwd: "" })); }}
                                                        startDecorator={<LockRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        endDecorator={
                                                            <Box onClick={() => setShowRegPwd(v => !v)} sx={{
                                                                cursor: "pointer", display: "flex", color: "rgba(0,0,0,0.3)",
                                                                "&:hover": { color: "rgba(0,0,0,0.6)" }, transition: "color 0.15s",
                                                            }}>
                                                                {showRegPwd
                                                                    ? <VisibilityOffRoundedIcon sx={{ fontSize: 17 }} />
                                                                    : <VisibilityRoundedIcon    sx={{ fontSize: 17 }} />
                                                                }
                                                            </Box>
                                                        }
                                                        sx={inputSx(!!regErrors.regPwd)}
                                                    />
                                                    <FieldError msg={regErrors.regPwd} />
                                                </FormControl>

                                                <FormControl>
                                                    <Stack direction={'row'} alignItems="center" sx={{ gap: 1, mt: 0.5 }}>
                                                        <Checkbox
                                                            size="sm"
                                                            checked={policyAgreed}
                                                            onChange={(e) => setPolicyAgreed(e.target.checked)}
                                                        />
                                                        <Typography level="body-sm" sx={{ lineHeight: 1.4 }}>
                                                            Я согласен с{" "}
                                                            <Link
                                                                component={RouterLink}
                                                                to="/privacy-policy"
                                                                sx={{ fontWeight: 600 }}
                                                            >
                                                                политикой конфиденциальности
                                                            </Link>
                                                        </Typography>
                                                    </Stack>
                                                </FormControl>

                                                <SubmitButton loading={loading} onClick={registerFn}>
                                                    Отправить анкету
                                                </SubmitButton>
                                            </Stack>
                                        )}

                                        {/* ──────── НЕТ ПАРОЛЯ ──────── */}
                                        {mode === "no-password" && (
                                            <Stack spacing={2.5} onKeyDown={e => { if (e.key === "Enter") requestFirstPassword(); }}>
                                                <Stack spacing={0.5}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: BRAND }}>
                                                        Первый вход
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "13px", color: "rgba(0,0,0,0.5)" }}>
                                                        Введите рабочую почту — мы создадим пароль.
                                                    </Typography>
                                                </Stack>

                                                <AnimatePresence mode="wait">
                                                    {error && (
                                                        <motion.div key="err"
                                                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                                                            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}
                                                        >
                                                            <Alert color="danger" variant="soft" sx={{ borderRadius: "12px", fontSize: "13px" }}>
                                                                {error}
                                                            </Alert>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                                
                                                {/* Тумблер */}
                                                <LoginTypeTabs value={noPassType} onChange={t => {
                                                    setNoPassType(t);
                                                    setError("");
                                                }} />

                                                {/* Поле ввода */}
                                                <AnimatePresence mode="wait">
                                                    {noPassType === "email" ? (
                                                        <motion.div key="np-email"
                                                            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: 10 }} transition={{ duration: 0.2 }}
                                                        >
                                                            <FormControl>
                                                                <FormLabel sx={labelSx}>Email</FormLabel>
                                                                <Input
                                                                    type="email" placeholder="ivan@example.com" autoFocus
                                                                    value={noPassEmail}
                                                                    onChange={e => setNoPassEmail(e.target.value)}
                                                                    startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                                    sx={inputSx(false)}
                                                                />
                                                            </FormControl>
                                                        </motion.div>
                                                    ) : (
                                                        <motion.div key="np-phone"
                                                            initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}
                                                        >
                                                            <FormControl>
                                                                <FormLabel sx={labelSx}>Телефон</FormLabel>
                                                                <MaskedInput
                                                                    mask="80000000000" placeholder="8__________" autoFocus
                                                                    value={noPassPhone}
                                                                    onChange={e => setNoPassPhone(e.target.value)}
                                                                    startDecorator={<PhoneRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                                    sx={inputSx(false)}
                                                                />
                                                            </FormControl>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>

                                                <SubmitButton loading={loading} onClick={requestFirstPassword}>
                                                    Получить пароль
                                                </SubmitButton>

                                                <Button
                                                    variant="plain" color="neutral" size="sm"
                                                    onClick={() => switchMode("login")}
                                                    sx={{ fontSize: "12px", color: `${BRAND}99`, "&:hover": { color: BRAND } }}
                                                >
                                                    ← Назад к авторизации
                                                </Button>
                                            </Stack>
                                        )}

                                        {/* ──────── ПАРОЛЬ СОЗДАН ──────── */}
                                        {mode === "password-created" && (
                                            <Stack spacing={2.5} alignItems="center">
                                                <Stack spacing={0.5} alignItems="center">
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: BRAND }}>
                                                        Ваш пароль готов
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "13px", color: "rgba(0,0,0,0.5)", textAlign: "center" }}>
                                                        Скопируйте и сохраните — он больше не будет показан.
                                                    </Typography>
                                                </Stack>

                                                <Box sx={{
                                                    width: "100%",
                                                    background: `linear-gradient(135deg, rgba(87,42,195,0.07) 0%, rgba(141,106,236,0.07) 100%)`,
                                                    border: `1.5px dashed rgba(87,42,195,0.25)`,
                                                    borderRadius: "16px",
                                                    p: 2.5, textAlign: "center",
                                                }}>
                                                    <Typography sx={{
                                                        letterSpacing: "0.18em", fontWeight: 800, fontSize: "1.6rem",
                                                        userSelect: "all", wordBreak: "break-all",
                                                        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`,
                                                        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                                                    }}>
                                                        {generatedPassword}
                                                    </Typography>
                                                </Box>

                                                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} style={{ width: "100%" }}>
                                                    <Button
                                                        fullWidth
                                                        variant={copied ? "soft" : "outlined"} color={copied ? "success" : "primary"}
                                                        startDecorator={copied
                                                            ? <CheckRoundedIcon sx={{ fontSize: 16 }} />
                                                            : <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                                                        }
                                                        onClick={handleCopy}
                                                        sx={{ borderRadius: "12px", py: 1.2, fontSize: "13px", fontWeight: 600 }}
                                                    >
                                                        {copied ? "Скопировано!" : "Копировать пароль"}
                                                    </Button>
                                                </motion.div>

                                                <SubmitButton onClick={handleGoLogin}>
                                                    Авторизоваться →
                                                </SubmitButton>
                                            </Stack>
                                        )}

                                        {/* ──────── ВОССТАНОВЛЕНИЕ ПАРОЛЯ ──────── */}
                                        {mode === "forgot-password" && (
                                            <Stack spacing={2.5} onKeyDown={e => { if (e.key === "Enter") requestPasswordReset(); }}>
                                                <Stack spacing={0.5}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: BRAND }}>
                                                        Восстановление пароля
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "13px", color: "rgba(0,0,0,0.5)" }}>
                                                        Введите телефон <b>и</b> email — оба должны совпадать с данными в системе.
                                                        Новый пароль придёт на почту.
                                                    </Typography>
                                                </Stack>

                                                <AnimatePresence mode="wait">
                                                    {error && (
                                                        <motion.div key="fp-err"
                                                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                                                            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}
                                                        >
                                                            <Alert color="danger" variant="soft" sx={{ borderRadius: "12px", fontSize: "13px" }}>
                                                                {error}
                                                            </Alert>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>

                                                {/* Телефон */}
                                                <FormControl>
                                                    <FormLabel sx={labelSx}>Телефон</FormLabel>
                                                    <MaskedInput
                                                        mask="80000000000" placeholder="8__________" autoFocus
                                                        value={forgotPhone}
                                                        onChange={e => { setForgotPhone(e.target.value); setError(""); }}
                                                        startDecorator={<PhoneRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        sx={inputSx(false)}
                                                    />
                                                </FormControl>

                                                {/* Email */}
                                                <FormControl>
                                                    <FormLabel sx={labelSx}>Email</FormLabel>
                                                    <Input
                                                        type="email" placeholder="ivan@example.com"
                                                        value={forgotEmail}
                                                        onChange={e => { setForgotEmail(e.target.value); setError(""); }}
                                                        startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                                        sx={inputSx(false)}
                                                    />
                                                </FormControl>

                                                <SubmitButton loading={loading} onClick={requestPasswordReset}>
                                                    Восстановить пароль
                                                </SubmitButton>

                                                <Button
                                                    variant="plain" color="neutral" size="sm"
                                                    onClick={() => switchMode("login")}
                                                    sx={{ fontSize: "12px", color: `${BRAND}99`, "&:hover": { color: BRAND } }}
                                                >
                                                    ← Назад к авторизации
                                                </Button>
                                            </Stack>
                                        )}

                                        {/* ──────── ПАРОЛЬ ОТПРАВЛЕН ──────── */}
                                        {mode === "password-sent" && (
                                            <Stack spacing={2.5} alignItems="center">
                                                <Stack spacing={0.5} alignItems="center">
                                                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: BRAND, textAlign: "center" }}>
                                                        Новый пароль отправлен ✉️
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "13px", color: "rgba(0,0,0,0.55)", textAlign: "center" }}>
                                                        Мы выслали новый пароль на&nbsp;
                                                        <b style={{ color: BRAND }}>{forgotEmailMasked}</b>.
                                                        <br />
                                                        Проверьте почту (в&nbsp;т.ч. папку «Спам») и&nbsp;войдите с&nbsp;новым паролем.
                                                    </Typography>
                                                </Stack>

                                                <SubmitButton loading={false} onClick={() => {
                                                    setForgotPhone(""); setForgotEmail(""); setForgotEmailMasked("");
                                                    switchMode("login");
                                                }}>
                                                    Перейти ко входу
                                                </SubmitButton>
                                            </Stack>
                                        )}

                                        {mode === "role-choice" && pendingAuthData && (
                                            <motion.div variants={itemVariants}>
                                                <Typography level="h4" sx={{ mb: 1 }}>Куда зайти?</Typography>
                                                <Typography level="body-sm" sx={{ mb: 2 }}>
                                                    У вас несколько ролей. Выберите раздел.
                                                </Typography>

                                                <Stack spacing={1.5}>
                                                    {pendingAuthData.positions.map(pos => (
                                                        <Box
                                                            key={pos}
                                                            onClick={() => chooseRole(pos)}
                                                            sx={{
                                                                p: 2.5, borderRadius: "16px", cursor: "pointer",
                                                                border: "2px solid rgba(87,42,195,0.15)",
                                                                background: "rgba(87,42,195,0.04)",
                                                                transition: "all .2s",
                                                                display: "flex", alignItems: "center", gap: 2,
                                                                "&:hover": {
                                                                    transform: "translateY(-2px)",
                                                                    borderColor: "#572AC3",
                                                                    boxShadow: "0 8px 24px rgba(87,42,195,0.18)",
                                                                },
                                                            }}
                                                        >
                                                            <Box sx={{ fontSize: 28 }}>{ROLE_META[pos]?.emoji || "👤"}</Box>
                                                            <Box>
                                                                <Typography level="title-md">{ROLE_META[pos]?.label || pos}</Typography>
                                                                <Typography level="body-xs" textColor="neutral.500">
                                                                    {ROLE_META[pos]?.desc}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </motion.div>
                                        )}

                                    </motion.div>
                                </AnimatePresence>
                            </Box>
                        </TiltCard>
                    </motion.div>

                    {/* ── Подпись ── */}
                    <motion.div variants={itemVariants}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 3 }}>
                            <ShieldRoundedIcon sx={{ fontSize: 13, color: "rgba(87,42,195,0.3)" }} />
                            <Typography sx={{ fontSize: "11px", letterSpacing: "0.02em", color: "rgba(87,42,195,0.35)" }}>
                                Защищённое соединение
                            </Typography>
                        </Stack>
                    </motion.div>

                </motion.div>
            </motion.div>
        </Box>
    );
}